import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { cn } from '../lib/cn.js';
import { Spotlight } from './spotlight.js';
import { Callout } from './callout.js';
const WTStageContext = createContext(null);
function safeStorageGet(key) {
    if (typeof window === 'undefined')
        return null;
    try {
        return window.localStorage.getItem(key);
    }
    catch {
        return null;
    }
}
function safeStorageSet(key, value) {
    if (typeof window === 'undefined')
        return;
    try {
        window.localStorage.setItem(key, value);
    }
    catch {
        // Quota / disabled / private — persistence is best-effort.
    }
}
function safeStorageRemove(key) {
    if (typeof window === 'undefined')
        return;
    try {
        window.localStorage.removeItem(key);
    }
    catch {
        // ditto
    }
}
export function Walkthrough({ steps, storageKey = 'leanopx-walkthrough', onComplete, onDismiss, }) {
    const stage = useContext(WTStageContext);
    const [active, setActive] = useState(true);
    const [index, setIndex] = useState(() => {
        const raw = safeStorageGet(storageKey);
        const n = raw == null ? 0 : Number(raw);
        return Number.isFinite(n) && n >= 0 && n < steps.length ? n : 0;
    });
    const [coords, setCoords] = useState(null);
    const step = steps[index];
    const measure = useCallback(() => {
        const stageEl = stage?.stageRef.current;
        if (!step || !stageEl) {
            setCoords(null);
            return;
        }
        const target = stageEl.querySelector(step.selector);
        if (!target) {
            setCoords(null);
            return;
        }
        const stageRect = stageEl.getBoundingClientRect();
        const r = target.getBoundingClientRect();
        const cx = r.left - stageRect.left + r.width / 2;
        const cy = r.top - stageRect.top + r.height / 2;
        setCoords({
            sx: cx,
            sy: cy,
            calloutTop: step.position === 'top' ? r.bottom - stageRect.top + 12 : r.top - stageRect.top - 8,
            calloutLeft: step.position === 'top'
                ? r.left - stageRect.left + r.width / 2 - 60
                : r.right - stageRect.left + 16,
        });
    }, [step, stage]);
    // Initial + on-step-change measurement.
    useLayoutEffect(() => {
        measure();
    }, [measure]);
    // Re-measure on window resize + scroll, and when the target's own
    // size changes (e.g. fonts loaded, content streamed in).
    useEffect(() => {
        if (!active || !step)
            return;
        function onShift() {
            measure();
        }
        window.addEventListener('resize', onShift);
        window.addEventListener('scroll', onShift, true);
        const stageEl = stage?.stageRef.current;
        let ro = null;
        if (stageEl && 'ResizeObserver' in window) {
            const target = stageEl.querySelector(step.selector);
            if (target) {
                ro = new ResizeObserver(onShift);
                ro.observe(target);
            }
        }
        return () => {
            window.removeEventListener('resize', onShift);
            window.removeEventListener('scroll', onShift, true);
            ro?.disconnect();
        };
    }, [active, step, stage, measure]);
    const advance = useCallback(() => {
        const next = index + 1;
        if (next >= steps.length) {
            safeStorageRemove(storageKey);
            setActive(false); // unmount so the consumer doesn't have to gate
            onComplete?.();
            return;
        }
        setIndex(next);
        safeStorageSet(storageKey, String(next));
    }, [index, onComplete, steps.length, storageKey]);
    const dismiss = useCallback(() => {
        safeStorageRemove(storageKey);
        setActive(false);
        onDismiss?.();
    }, [onDismiss, storageKey]);
    // Global keyboard handler. Gated to avoid stealing input from
    // editable fields and from anything that already prevented default.
    useEffect(() => {
        if (!active)
            return;
        function isEditable(target) {
            if (!(target instanceof HTMLElement))
                return false;
            if (target.isContentEditable)
                return true;
            const tag = target.tagName;
            return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
        }
        function onKey(e) {
            if (e.defaultPrevented)
                return;
            if (e.metaKey || e.ctrlKey || e.altKey)
                return;
            if (isEditable(e.target))
                return;
            if (e.key === 'Enter' || e.key === 'ArrowRight') {
                e.preventDefault();
                advance();
            }
            else if (e.key === 'Escape') {
                e.preventDefault();
                dismiss();
            }
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [active, advance, dismiss]);
    if (!active || !step)
        return null;
    return (_jsx("div", { className: cn('pointer-events-none absolute inset-0 z-40 isolate overflow-hidden rounded-3'), children: coords && (_jsxs(_Fragment, { children: [_jsx(Spotlight, { style: {
                        '--sx': `${coords.sx}px`,
                        '--sy': `${coords.sy}px`,
                    } }), _jsxs(Callout, { arrow: step.position === 'top' ? 'top' : 'left', className: "pointer-events-auto", style: { top: coords.calloutTop, left: coords.calloutLeft }, children: [step.body, _jsxs("div", { className: "mt-4 flex items-center justify-between font-mono text-2xs uppercase tracking-[0.12em] text-[var(--color-ink-muted)]", children: [_jsxs("span", { children: [index + 1, " / ", steps.length] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { type: "button", onClick: dismiss, className: "rounded-2 border border-[var(--color-line-2)] px-2.5 py-1 text-2xs uppercase tracking-[0.1em] text-[var(--color-ink-muted)] hover:border-[var(--color-line-strong)] hover:text-[var(--color-ink)]", "aria-label": "Dismiss walkthrough", children: "Esc" }), _jsx("button", { type: "button", onClick: advance, className: "rounded-2 bg-[var(--color-accent)] px-3 py-1.5 text-2xs font-medium uppercase tracking-[0.1em] text-[var(--color-accent-ink)] hover:bg-[var(--color-accent-bright)]", children: index + 1 === steps.length ? 'Done' : 'Next ↵' })] })] })] })] })) }));
}
/**
 * WTStage — relative + isolation container the Walkthrough positions
 * inside. Wrap whatever surface you want to spotlight; child content
 * goes inside the stage, and the `<Walkthrough>` is its sibling /
 * child. Exposes the stage ref via context so the orchestrator can
 * query for spotlight targets in the stage's DOM subtree (not the
 * overlay's).
 */
export function WTStage({ children, className }) {
    const stageRef = useRef(null);
    const value = useMemo(() => ({ stageRef }), []);
    return (_jsx(WTStageContext.Provider, { value: value, children: _jsx("div", { ref: stageRef, className: cn('relative isolate overflow-hidden rounded-3 bg-[var(--color-canvas)]', className), children: children }) }));
}
/**
 * Helper: returns the persisted step index for a walkthrough's storage
 * key, or null if not started / completed. Useful for the
 * `/admin/settings` replay affordance. Safe in storage-disabled
 * contexts (returns null on any access failure).
 */
export function getWalkthroughProgress(storageKey) {
    const raw = safeStorageGet(storageKey);
    if (raw == null)
        return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : null;
}
